// src/components/ProjectManagement/LoadBalancingView.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';

// Global cache
let cachedWorkersData = [];
let cachedUrgentTasks = [];
let lastWorkersFetch = 0;
let lastUrgentFetch = 0;
let isFetchingWorkers = false;
let workersFetchPromise = null;
const CACHE_DURATION = 30000;

const LoadBalancingView = ({ currentUser, userRole }) => {
  const [workers, setWorkers] = useState(cachedWorkersData);
  const [urgentTasks, setUrgentTasks] = useState(cachedUrgentTasks);
  const [loading, setLoading] = useState(!cachedWorkersData.length);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tooltipVisible, setTooltipVisible] = useState(false);
  
  const hideTimeoutRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // ========== HELPER FUNCTIONS ==========
  
  const getTaskDisplayDate = useCallback((task) => {
    if (task.status === 'completed' && task.completedAt) return task.completedAt;
    if (task.endDateObj) return task.endDateObj;
    if (task.dueDate) return task.dueDate;
    if (task.startDateObj) return task.startDateObj;
    if (task.endDate) {
      if (task.endDate.toDate) return task.endDate.toDate();
      return new Date(task.endDate);
    }
    if (task.startDate) {
      if (task.startDate.toDate) return task.startDate.toDate();
      return new Date(task.startDate);
    }
    return task.createdAt?.toDate?.() || new Date();
  }, []);

  const getTaskColor = useCallback((task) => {
    if (!task) return '#ebedf0';
    const now = new Date();
    const dueDate = task.endDateObj || (task.endDate?.toDate?.() || (task.endDate ? new Date(task.endDate) : null));
    
    if (task.status === 'pending' && dueDate && dueDate < now) return '#ef4444';
    if (task.status === 'completed') return '#10b981';
    if (task.status === 'ongoing') return '#3b82f6';
    if (task.status === 'pending') return '#f59e0b';
    return '#ebedf0';
  }, []);

  const getTaskStatusText = useCallback((status) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'ongoing': return 'In Progress';
      case 'completed': return 'Completed';
      default: return status || 'Unknown';
    }
  }, []);

  const getAssignedNames = useCallback((task) => {
    if (task.assignedToNames && Array.isArray(task.assignedToNames) && task.assignedToNames.length > 0) {
      return task.assignedToNames.join(', ');
    }
    if (task.assignedToName) return task.assignedToName;
    return 'Unassigned';
  }, []);

  // ========== TOOLTIP HANDLERS ==========
  
  const showTooltip = useCallback((e, task) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.right + 15;
    const y = rect.top - 20;
    
    setHoverPosition({ x, y });
    setHoveredTask(task);
    setTooltipVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltipVisible(false);
      setHoveredTask(null);
    }, 300);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    setTooltipVisible(false);
    setHoveredTask(null);
  }, []);

  // ========== TASK FETCHING FUNCTIONS ==========
  
  const getTasksForWorker = useCallback((worker, date) => {
    if (!worker || !worker.tasks || !date) return [];
    const dateStr = date.toDateString();
    
    if (!worker._taskDateCache) {
      worker._taskDateCache = new Map();
      worker.tasks.forEach(task => {
        const taskDate = getTaskDisplayDate(task);
        if (taskDate) {
          const key = taskDate.toDateString();
          if (!worker._taskDateCache.has(key)) {
            worker._taskDateCache.set(key, []);
          }
          worker._taskDateCache.get(key).push(task);
        }
      });
    }
    
    return worker._taskDateCache.get(dateStr) || [];
  }, [getTaskDisplayDate]);

  const getAllTasksForDate = useCallback((date) => {
    if (!date) return [];
    const dateStr = date.toDateString();
    const allTasksMap = new Map();
    
    workers.forEach(worker => {
      if (!worker._taskDateCache) {
        worker._taskDateCache = new Map();
        worker.tasks.forEach(task => {
          const taskDate = getTaskDisplayDate(task);
          if (taskDate) {
            const key = taskDate.toDateString();
            if (!worker._taskDateCache.has(key)) {
              worker._taskDateCache.set(key, []);
            }
            worker._taskDateCache.get(key).push(task);
          }
        });
      }
      
      const workerTasks = worker._taskDateCache.get(dateStr) || [];
      workerTasks.forEach(task => {
        const taskId = task.id;
        if (!allTasksMap.has(taskId)) {
          allTasksMap.set(taskId, { 
            ...task, 
            assignedToNamesList: [getAssignedNames(task)]
          });
        } else {
          const existing = allTasksMap.get(taskId);
          const assignedName = getAssignedNames(task);
          if (assignedName !== 'Unassigned' && !existing.assignedToNamesList.includes(assignedName)) {
            existing.assignedToNamesList.push(assignedName);
          }
        }
      });
    });
    
    const allTasks = Array.from(allTasksMap.values());
    allTasks.forEach(task => {
      task.assignedToNames = task.assignedToNamesList;
    });
    
    return allTasks;
  }, [workers, getTaskDisplayDate, getAssignedNames]);

  const getTasksForDate = useCallback((date) => {
    if (!date) return [];
    const selectedWorker = selectedUserId === 'all' ? null : workers.find(w => w.id === selectedUserId);
    if (selectedUserId === 'all') {
      return getAllTasksForDate(date);
    }
    return selectedWorker ? getTasksForWorker(selectedWorker, date) : [];
  }, [selectedUserId, workers, getTasksForWorker, getAllTasksForDate]);

  // ========== DATA FETCHING ==========
  
  const fetchWorkersData = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    if (!forceRefresh && cachedWorkersData.length > 0 && (now - lastWorkersFetch) < CACHE_DURATION) {
      setWorkers(cachedWorkersData);
      setLoading(false);
      return cachedWorkersData;
    }
    
    if (isFetchingWorkers) {
      if (workersFetchPromise) {
        const result = await workersFetchPromise;
        setWorkers(result);
        setLoading(false);
        return result;
      }
      return;
    }
    
    isFetchingWorkers = true;
    setLoading(true);
    
    workersFetchPromise = (async () => {
      try {
        const [usersSnapshot, tasksSnapshot] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'tasks'))
        ]);
        
        const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const allTasks = tasksSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          dueDate: doc.data().deadline?.toDate?.() || null,
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          completedAt: doc.data().completedAt?.toDate?.() || null,
          startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
          endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
          assignedTo: doc.data().assignedTo || [],
          assignedToNames: doc.data().assignedToNames || []
        }));
        
        const tasksByWorkerMap = new Map();
        
        allTasks.forEach(task => {
          let assignedWorkers = [];
          
          if (Array.isArray(task.assignedTo) && task.assignedTo.length > 0) {
            assignedWorkers = task.assignedTo;
          } else if (typeof task.assignedTo === 'string' && task.assignedTo) {
            assignedWorkers = [task.assignedTo];
          } else if (task.assignedTo && typeof task.assignedTo === 'object' && !Array.isArray(task.assignedTo)) {
            assignedWorkers = Object.values(task.assignedTo);
          }
          
          assignedWorkers.forEach(workerId => {
            if (!tasksByWorkerMap.has(workerId)) {
              tasksByWorkerMap.set(workerId, []);
            }
            tasksByWorkerMap.get(workerId).push(task);
          });
        });
        
        const workersWithTasks = usersData.map(user => ({
          ...user,
          tasks: tasksByWorkerMap.get(user.id) || []
        }));
        
        cachedWorkersData = workersWithTasks;
        lastWorkersFetch = Date.now();
        
        return workersWithTasks;
      } catch (error) {
        console.error('Error fetching data:', error);
        return cachedWorkersData.length ? cachedWorkersData : [];
      } finally {
        isFetchingWorkers = false;
        workersFetchPromise = null;
        setLoading(false);
      }
    })();
    
    const result = await workersFetchPromise;
    setWorkers(result);
    return result;
  }, []);

  const fetchUrgentTasks = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    if (!forceRefresh && cachedUrgentTasks.length > 0 && (now - lastUrgentFetch) < CACHE_DURATION) {
      setUrgentTasks(cachedUrgentTasks);
      return cachedUrgentTasks;
    }
    
    try {
      const urgentQuery = query(
        collection(db, 'tasks'), 
        where('priority', '==', 'urgent'), 
        where('status', '==', 'pending')
      );
      
      const snapshot = await getDocs(urgentQuery);
      const tasks = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
        assignedToNames: doc.data().assignedToNames || []
      }));
      
      cachedUrgentTasks = tasks;
      lastUrgentFetch = Date.now();
      setUrgentTasks(tasks);
      
      return tasks;
    } catch (error) {
      console.error('Error fetching urgent tasks:', error);
      return cachedUrgentTasks;
    }
  }, []);

  // ========== CALENDAR HELPER FUNCTIONS ==========
  
  const getVisibleDates = useCallback(() => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch(viewMode) {
      case 'today': {
        dates.push(new Date(today));
        break;
      }
      case 'week': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        for (let i = 0; i < 7; i++) {
          const date = new Date(startOfWeek);
          date.setDate(startOfWeek.getDate() + i);
          dates.push(date);
        }
        break;
      }
      case 'lastWeek': {
        const startOfLastWeek = new Date(today);
        startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
        for (let i = 0; i < 7; i++) {
          const date = new Date(startOfLastWeek);
          date.setDate(startOfLastWeek.getDate() + i);
          dates.push(date);
        }
        break;
      }
      case 'month': {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          dates.push(new Date(year, month, i));
        }
        break;
      }
      case 'lastMonth': {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() - 1;
        const adjustedMonth = month < 0 ? 11 : month;
        const adjustedYear = month < 0 ? year - 1 : year;
        const daysInMonth = new Date(adjustedYear, adjustedMonth + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          dates.push(new Date(adjustedYear, adjustedMonth, i));
        }
        break;
      }
      case 'year': {
        const year = currentDate.getFullYear();
        for (let month = 0; month < 12; month++) {
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          for (let day = 1; day <= daysInMonth; day++) {
            dates.push(new Date(year, month, day));
          }
        }
        break;
      }
      default: break;
    }
    return dates;
  }, [viewMode, currentDate]);

  const visibleDates = useMemo(() => getVisibleDates(), [getVisibleDates]);
  
  const groupedByWeek = useMemo(() => {
    if (viewMode !== 'month' && viewMode !== 'lastMonth' && viewMode !== 'year') return null;
    
    const weeks = [];
    let currentWeek = [];
    
    visibleDates.forEach(date => {
      if (currentWeek.length === 0) {
        currentWeek.push(date);
      } else if (date.getDay() === 0) {
        weeks.push([...currentWeek]);
        currentWeek = [date];
      } else {
        currentWeek.push(date);
      }
    });
    
    if (currentWeek.length > 0) weeks.push(currentWeek);
    return weeks;
  }, [visibleDates, viewMode]);

  // ========== NAVIGATION FUNCTIONS ==========
  
  const goToPrevious = useCallback(() => {
    if (viewMode === 'month' || viewMode === 'lastMonth') {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    } else if (viewMode === 'year') {
      setCurrentDate(prev => new Date(prev.getFullYear() - 1, 0, 1));
    }
  }, [viewMode]);

  const goToNext = useCallback(() => {
    if (viewMode === 'month' || viewMode === 'lastMonth') {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    } else if (viewMode === 'year') {
      setCurrentDate(prev => new Date(prev.getFullYear() + 1, 0, 1));
    }
  }, [viewMode]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
    setViewMode('today');
  }, []);

  // ========== EFFECTS ==========
  
  useEffect(() => {
    fetchWorkersData();
    fetchUrgentTasks();
    
    const unsubscribe = onSnapshot(collection(db, 'tasks'), () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchWorkersData(true);
        fetchUrgentTasks(true);
      }, 1000);
    });
    
    return () => {
      unsubscribe();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [fetchWorkersData, fetchUrgentTasks]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // ========== RENDER ==========
  
  if (loading && !cachedWorkersData.length) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading workload data...</p>
      </div>
    );
  }

  const selectedWorker = selectedUserId === 'all' ? null : workers.find(w => w.id === selectedUserId);
  const totalTasksAllWorkers = workers.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);

  // Task Card Component
  const TaskCard = ({ task, showAssignedNames = false }) => {
    const handleMouseEnter = (e) => {
      e.stopPropagation();
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverPosition({ x: rect.right + 15, y: rect.top - 20 });
      setHoveredTask(task);
      setTooltipVisible(true);
    };

    const handleMouseLeave = () => {
      hideTimeoutRef.current = setTimeout(() => {
        setTooltipVisible(false);
        setHoveredTask(null);
      }, 300);
    };

    const handleScaleEnter = (e) => {
      e.currentTarget.style.transform = 'scale(1.02)';
    };

    const handleScaleLeave = (e) => {
      e.currentTarget.style.transform = 'scale(1)';
    };

    const taskColor = getTaskColor(task);
    const isLightColor = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'].includes(taskColor);
    
    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseEnterCapture={handleScaleEnter}
        onMouseLeaveCapture={handleScaleLeave}
        style={{
          background: taskColor,
          color: isLightColor ? 'white' : '#333',
          padding: '6px 8px',
          borderRadius: '8px',
          marginBottom: '6px',
          fontSize: '11px',
          cursor: 'pointer',
          transition: 'transform 0.1s ease'
        }}
      >
        <div style={{ fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.title}
        </div>
        <div style={{ fontSize: '9px', opacity: 0.9 }}>
          {getTaskStatusText(task.status)}
        </div>
        {showAssignedNames && task.assignedToNames && task.assignedToNames.length > 0 && (
          <div style={{ fontSize: '8px', marginTop: '2px', opacity: 0.8 }}>
            👥 {Array.isArray(task.assignedToNames) ? task.assignedToNames.join(', ') : task.assignedToNames}
          </div>
        )}
      </div>
    );
  };

  // Tooltip Component
  const Tooltip = () => {
    if (!tooltipVisible || !hoveredTask) return null;
    
    return (
      <div 
        style={{ 
          position: 'fixed', 
          top: hoverPosition.y, 
          left: hoverPosition.x,
          zIndex: 10000,
          background: '#1f2937',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          minWidth: '260px',
          maxWidth: '320px',
          boxShadow: '0 20px 35px rgba(0,0,0,0.4)',
          pointerEvents: 'auto',
          animation: 'fadeIn 0.15s ease'
        }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', paddingRight: '20px' }}>
          {hoveredTask.title}
        </div>
        <div style={{ fontSize: '11px', marginBottom: '8px', color: '#9ca3af' }}>
          {hoveredTask.description || 'No description'}
        </div>
        <div style={{ fontSize: '11px', marginBottom: '4px' }}>
          📁 {hoveredTask.projectName || 'Unknown Project'}
        </div>
        <div style={{ fontSize: '11px', marginBottom: '4px' }}>
          📅 Due: {hoveredTask.endDateObj?.toLocaleDateString() || hoveredTask.endDate?.toDate?.()?.toLocaleDateString() || 'No deadline'}
        </div>
        <div style={{ fontSize: '11px', marginBottom: '4px' }}>
          🏷️ Category: {hoveredTask.category || 'Uncategorized'}
        </div>
        <div style={{ fontSize: '11px' }}>
          👥 Assigned to: {getAssignedNames(hoveredTask)}
        </div>
        <div style={{ fontSize: '10px', marginTop: '6px', color: '#6b7280' }}>
          Status: {getTaskStatusText(hoveredTask.status)}
        </div>
      </div>
    );
  };

  return (
    <div className="load-balancing-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Tooltip />

      {/* Urgent Tasks Section */}
      {urgentTasks.length > 0 && (
        <div className="urgent-section">
          <div className="urgent-header">
            <h3>🚨 Urgent Tasks</h3>
            <span className="urgent-count">{urgentTasks.length}</span>
          </div>
          <div className="urgent-list" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {urgentTasks.map(task => {
              const handleMouseEnter = (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoverPosition({ x: rect.right + 15, y: rect.top - 20 });
                setHoveredTask(task);
                setTooltipVisible(true);
              };
              const handleMouseLeave = () => {
                hideTimeoutRef.current = setTimeout(() => {
                  setTooltipVisible(false);
                  setHoveredTask(null);
                }, 300);
              };
              
              return (
                <div 
                  key={task.id} 
                  className="urgent-item" 
                  style={{ flex: '1 1 auto', minWidth: '200px', cursor: 'pointer' }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="urgent-dot">🔴</span>
                  <span className="urgent-title"><strong>{task.title}</strong></span>
                  <span className="urgent-date">
                    Due: {task.endDateObj?.toLocaleDateString() || 'No date'}
                  </span>
                  {task.projectName && (
                    <span className="urgent-project" style={{ fontSize: '10px', color: '#666', marginLeft: '8px' }}>
                      📁 {task.projectName}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        background: 'white',
        padding: '16px 20px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => { setViewMode('today'); goToToday(); }}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'today' ? '#800020' : '#f3f4f6', color: viewMode === 'today' ? 'white' : '#374151' }}
          >
            📅 Today
          </button>
          <button 
            onClick={() => setViewMode('week')}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'week' ? '#800020' : '#f3f4f6', color: viewMode === 'week' ? 'white' : '#374151' }}
          >
            📆 This Week
          </button>
          <button 
            onClick={() => setViewMode('lastWeek')}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'lastWeek' ? '#800020' : '#f3f4f6', color: viewMode === 'lastWeek' ? 'white' : '#374151' }}
          >
            📆 Last Week
          </button>
          <button 
            onClick={() => setViewMode('month')}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'month' ? '#800020' : '#f3f4f6', color: viewMode === 'month' ? 'white' : '#374151' }}
          >
            📅 This Month
          </button>
          <button 
            onClick={() => setViewMode('lastMonth')}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'lastMonth' ? '#800020' : '#f3f4f6', color: viewMode === 'lastMonth' ? 'white' : '#374151' }}
          >
            📆 Last Month
          </button>
          <button 
            onClick={() => setViewMode('year')}
            style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: 'none', fontWeight: '500', background: viewMode === 'year' ? '#800020' : '#f3f4f6', color: viewMode === 'year' ? 'white' : '#374151' }}
          >
            📅 Year
          </button>
        </div>
        
        {(viewMode === 'month' || viewMode === 'lastMonth' || viewMode === 'year') && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={goToPrevious} style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white' }}>← Previous</button>
            <span style={{ fontWeight: '600', minWidth: '120px', textAlign: 'center' }}>
              {viewMode === 'year' ? currentDate.getFullYear() : monthNames[currentDate.getMonth()] + ' ' + currentDate.getFullYear()}
            </span>
            <button onClick={goToNext} style={{ padding: '8px 16px', cursor: 'pointer', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white' }}>Next →</button>
          </div>
        )}
      </div>

      {/* Main Content: User List + Calendar */}
      <div style={{ display: 'flex', gap: '20px', minHeight: '600px' }}>
        {/* User Sidebar */}
        <div style={{ 
          width: '280px', 
          background: 'white', 
          borderRadius: '16px', 
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ 
            padding: '16px', 
            background: 'linear-gradient(135deg, #800020, #5a0016)', 
            color: 'white',
            fontWeight: '600'
          }}>
            👥 Team Members ({workers.length})
          </div>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <div
              onClick={() => setSelectedUserId('all')}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                background: selectedUserId === 'all' ? 'linear-gradient(135deg, rgba(128,0,32,0.1), rgba(128,0,32,0.05))' : 'white',
                borderLeft: selectedUserId === 'all' ? '3px solid #800020' : '3px solid transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedUserId !== 'all') e.currentTarget.style.background = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                if (selectedUserId !== 'all') e.currentTarget.style.background = 'white';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #800020, #5a0016)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>
                  📊
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>All Team Members</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>Complete team view</div>
                  <div style={{ fontSize: '11px', color: '#800020', marginTop: '2px' }}>
                    📋 {totalTasksAllWorkers} total tasks
                  </div>
                </div>
              </div>
            </div>
            
            {workers.map(worker => (
              <div
                key={worker.id}
                onClick={() => setSelectedUserId(worker.id)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  background: selectedUserId === worker.id ? 'linear-gradient(135deg, rgba(128,0,32,0.1), rgba(128,0,32,0.05))' : 'white',
                  borderLeft: selectedUserId === worker.id ? '3px solid #800020' : '3px solid transparent',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedUserId !== worker.id) e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (selectedUserId !== worker.id) e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #800020, #5a0016)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    {worker.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{worker.name || 'Unnamed'}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{worker.role?.replace('-', ' ') || 'Staff'}</div>
                    <div style={{ fontSize: '11px', color: '#800020', marginTop: '2px' }}>
                      📋 {worker.tasks?.length || 0} tasks
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar View */}
        <div style={{ 
          flex: 1, 
          background: 'white', 
          borderRadius: '16px', 
          overflow: 'auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px'
        }}>
          {selectedUserId === 'all' ? (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
              📊 Showing tasks for <strong>ALL team members</strong> ({workers.length} workers)
            </div>
          ) : selectedWorker ? (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
              👤 Showing tasks for <strong>{selectedWorker.name}</strong> ({selectedWorker.role?.replace('-', ' ') || 'Staff'})
            </div>
          ) : null}
          
          {/* Week/Today/LastWeek View */}
          {(viewMode === 'week' || viewMode === 'today' || viewMode === 'lastWeek') && (
            <div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${visibleDates.length}, 1fr)`,
                gap: '1px',
                background: '#f0f0f0',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                {visibleDates.map((date, idx) => {
                  const tasks = getTasksForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  return (
                    <div key={idx} style={{ background: 'white' }}>
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '12px', 
                        fontWeight: '600',
                        borderBottom: '1px solid #f0f0f0',
                        background: isToday ? '#fef3c7' : 'transparent'
                      }}>
                        <div>{weekDays[date.getDay()]}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{date.getDate()}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{monthNames[date.getMonth()]}</div>
                      </div>
                      <div style={{ minHeight: '200px', padding: '8px' }}>
                        {tasks.map(task => (
                          <TaskCard 
                            key={task.id} 
                            task={task} 
                            showAssignedNames={selectedUserId === 'all'}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month/Year View */}
          {(viewMode === 'month' || viewMode === 'lastMonth' || viewMode === 'year') && groupedByWeek && (
            <div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)',
                textAlign: 'center',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#6b7280'
              }}>
                {weekDays.map(day => (
                  <div key={day} style={{ padding: '8px' }}>{day}</div>
                ))}
              </div>
              
              {groupedByWeek.map((week, weekIdx) => (
                <div key={weekIdx} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: '#f0f0f0', marginBottom: '1px' }}>
                  {week.map((date, dayIdx) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    const tasks = getTasksForDate(date);
                    return (
                      <div 
                        key={dayIdx} 
                        style={{ 
                          background: 'white', 
                          minHeight: '120px',
                          padding: '8px',
                          border: isToday ? '2px solid #f59e0b' : 'none',
                          position: 'relative'
                        }}
                      >
                        <div style={{ 
                          fontWeight: isToday ? 'bold' : 'normal',
                          color: isToday ? '#f59e0b' : '#333',
                          marginBottom: '6px'
                        }}>
                          {date.getDate()}
                        </div>
                        {tasks.slice(0, 3).map(task => {
                          const handleMouseEnter = (e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoverPosition({ x: rect.right + 15, y: rect.top - 20 });
                            setHoveredTask(task);
                            setTooltipVisible(true);
                          };
                          const handleMouseLeave = () => {
                            hideTimeoutRef.current = setTimeout(() => {
                              setTooltipVisible(false);
                              setHoveredTask(null);
                            }, 300);
                          };
                          
                          return (
                            <div
                              key={task.id}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                              style={{
                                background: getTaskColor(task),
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {task.title}
                            </div>
                          );
                        })}
                        {tasks.length > 3 && (
                          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                            +{tasks.length - 3} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadBalancingView;